import { FeatureTypes, CapabilitiesDocumentation, WorkflowCapabilitiesDocumentation } from '@/components/Billing/billing-types';
const BASE_URL = process.env.VUE_APP_APP_URL;
export const CAPABILITIES: CapabilitiesDocumentation[] = [
	{
		id: FeatureTypes.CustomerCustomDomains,
		title: 'Custom domains for portals',
		description: 'An admin in your workspace has set up a custom domain for hosting customer portals.',
		howToFix:
			'Go to workspace settings and delete all custom domains. Any portals previously hosted at this domain will no longer be usable. Their default Ply URLs, found in the feature details, still work.',
		link: `${BASE_URL}settings/custom-domains`,
		linkText: 'Go to domain settings',
	},
	{
		id: FeatureTypes.CustomerCustomEmailDomains,
		title: 'Custom email domains for portals',
		description: 'An admin in your workspace has set up a custom email domain for transactional emails.',
		howToFix: 'Go to workspace settings and delete all custom email domains.',
		link: `${BASE_URL}settings/email-domains`,
		linkText: 'Go to email domains settings',
	},
	{
		id: FeatureTypes.Groups,
		title: 'User groups',
		description:
			'An admin in your workspace has created a group of workspace members that you can reference for functions like feature sharing, invitation to spaces, and shared inboxes.',
		howToFix:
			'Go to group settings and delete all groups. Group members will lose access to all of the assets, spaces, and inboxes that were shared to them via the group (unless they independently had access as users).',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.SyncedGroups,
		title: 'Synced user groups',
		description:
			'An admin in your workspace has created a group of workspace members whose membership is synced with a user list from an external app.',
		howToFix:
			'Go to group settings and delete all synced groups. Group members will lose access to all of the assets, spaces, and inboxes that were shared to them via the group (unless they independently had access as users).',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.GroupUsagePermissions,
		title: 'Group usage permissions',
		description: 'An admin in your workspace has applied fine-grained permission restrictions to a user group.',
		howToFix: 'Ensure that all uses are allowed for all user groups.',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.PremiumUsersCount,
		title: 'Excessive premium members',
		description: 'Your premium user count (N) is greater than what is allowed in the selected team features plan.',
		howToFix: 'Reduce your active premium member count to N. You can do this by changing member roles or deactivating members.',
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.StandardUsersCount,
		title: 'Excessive standard members',
		description: 'Your standard user count (N) is greater than what is allowed in the selected team features plan.',
		howToFix: 'Reduce your active standard member count to N. You can do this by changing member roles or deactivating members.',
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.LiteUsersCount,
		title: 'Excessive lite members',
		description: 'Your lite user count (N) is greater than what is allowed in the selected team features plan.',
		howToFix: 'Reduce your active lite member count to N. You can do this by changing member roles or deactivating members.',
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.UserHierarchy,
		title: 'Advanced workspace roles',
		description: 'There are members in your workspace with advanced creator roles (not just workspace admin).',
		howToFix:
			"Ensure that all creators have the workspace role of admin or downgrade them to end users. Make sure to check deactivated users' roles.",
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.CreatorSpacePermissions,
		title: 'Advanced user roles in creator spaces',
		description: 'There are shared creator spaces in your workspace where collaborators have advanced user roles (not just space admin).',
		howToFix: 'Ensure that all collaborators in all shared spaces have the role space admin.',
		link: `${BASE_URL}space/everything/all-spaces/`,
		linkText: 'Go to spaces',
	},
	{
		id: FeatureTypes.SensitiveConnections,
		title: 'Sensitive connections in creator spaces',
		description: 'There are shared creator spaces in your workspace with connections that have been set as sensitive.',
		howToFix: 'Ensure that all connections in all shared spaces are accessible to all creators.',
		link: `${BASE_URL}space/everything/all-spaces/`,
		linkText: 'Go to spaces',
	},
	{
		id: FeatureTypes.SharedNotificationInboxes,
		title: 'Shared notification inboxes',
		description: 'A user in your workspace has created a shared notification inbox.',
		howToFix: 'Talk with support to delete the shared inbox.',
	},
];

export const CAPABILITIES_MAP: Record<FeatureTypes, CapabilitiesDocumentation> = CAPABILITIES.reduce((acc, curr) => {
	acc[curr.id] = curr;
	return acc;
}, {} as Record<FeatureTypes, CapabilitiesDocumentation>);

export const WORKFLOW_CAPABILITIES: WorkflowCapabilitiesDocumentation[] = [
	{
		id: FeatureTypes.SubFlows,
		title: 'SubFlows',
		description:
			'A Ply subflow is a simple way to bundle a set of actions in a workflow so you can easily reuse them in other workflows without rebuilding everything from scratch.',
		howToFix: 'Disable the feature or republish after deleting subflow steps.',
	},
	{
		id: FeatureTypes.TeamOutgoingApiActions,
		title: 'Custom webhooks and API calls',
		description: 'Seamlessly connect to your internal apps and databases to send or retrieve data via webhooks and API calls.',
		howToFix: 'Disable the feature or republish after deleting custom API actions.',
	},
	{
		id: FeatureTypes.CustomerOutgoingApiActions,
		title: 'Custom webhooks and API calls',
		description: 'Seamlessly connect to your internal apps and databases to send or retrieve data via webhooks and API calls.',
		howToFix: 'Disable the feature or republish after deleting custom API actions.',
	},
	{
		id: FeatureTypes.TeamCustomCodeActions,
		title: 'Javascript code block',
		description: 'Enhance your workflows by running JavaScript in the cloud, ideal for managing complex data manipulations and API logic.',
		howToFix: 'Disable the feature or republish after deleting code block steps.',
	},
	{
		id: FeatureTypes.CustomerCustomCodeActions,
		title: 'Javascript code block',
		description: 'Enhance your workflows by running JavaScript in the cloud, ideal for managing complex data manipulations and API logic.',
		howToFix: 'Disable the feature or republish after deleting code block steps.',
	},
	{
		id: FeatureTypes.CustomerUrlParameters,
		title: 'URL parameters',
		description: "Tailor every customer's portal experience by passing custom data through the URL.",
		howToFix: 'Disable the feature or republish after deleting URL parameters from the URL trigger step.',
	},
	{
		id: FeatureTypes.CustomerIframeBlock,
		title: 'Iframe block in an interface step',
		description: 'Let customers access and interact with external websites through an iframe block in an interface step.',
		howToFix: 'Disable the feature or republish after deleting all iframe blocks from interfaces.',
	},
	{
		id: FeatureTypes.CustomerMiniApps,
		title: 'Mini-app step',
		description:
			'User-facing steps with predefined functionality, including Calendly and HubSpot schedulers, Stripe checkout, image editor, table mapper for CSV imports, and more.',
		howToFix: 'Disable the feature or republish after deleting all mini-app steps.',
	},
	{
		id: FeatureTypes.CustomerRedirect,
		title: 'Portal URL redirect',
		description: 'Redirect your customers to custom URLs upon portal completion.',
		howToFix: 'Disable the feature or republish after deleting all URL redirect steps.',
	},
	{
		id: FeatureTypes.CustomerExtraPremiumApps,
		title: 'Extra premium apps',
		description:
			'Use premium integrations in your portal workflows, like Salesforce and our analytics event dispatcher – great for tracking conversions and other custom events.',
		howToFix: 'Disable the feature or republish after deleting all Salesforce and event dispatcher steps.',
	},
	{
		id: FeatureTypes.CustomerAuthEmailSignIn,
		title: 'Email authentication',
		description: 'Verify email addresses at the start of the portal workflow to securely display personal data to your customers.',
		howToFix: 'Disable the feature or republish after disabling email authentication.',
	},
	{
		id: FeatureTypes.CustomerAuthSMSSignIn,
		title: 'SMS authentication',
		description: 'Verify SMS numbers at the start of the portal workflow to securely display personal data to your customers.',
		howToFix: 'Disable the feature or republish after disabling SMS authentication.',
	},
];

export const WORKFLOW_CAPABILITIES_MAP: Record<FeatureTypes, WorkflowCapabilitiesDocumentation> = WORKFLOW_CAPABILITIES.reduce(
	(acc, curr) => {
		acc[curr.id] = curr;
		return acc;
	},
	{} as Record<FeatureTypes, WorkflowCapabilitiesDocumentation>
);
